/** @jsxImportSource @emotion/react */
import {Entitlement, EntitlementHotlist, EntitlementShare} from '../../models';
import {tableStyle} from '../../../../resortMappingTool/DynamicTable';
import {useSelector} from 'react-redux';
import {css} from '@emotion/react';
import {getGuestHotlists, isActive, isDeferred, sortByExpirationDateAsc} from '../../module';
import {GuestDetails} from '../../../guestDetails';
import {useEffect, useMemo, useState} from 'react';
import {IAppState} from '../../../../core/redux/IAppState';
import {getLoading} from '../../../../core/redux/api/module';
import {ACTION_TYPE} from '../../../../core/redux/actionType';
import {FNF_PAGES, fnfIsHotlisted} from './module';
import { getResource } from '../../../../language/resources/getResource';
import { FnFEntitlementsRow } from './FnFEntitlementsRow';
import ImmutableMap from '../../../../language/ImmutableMap';


type Props = {
  entitlements: Entitlement[];
  guest: GuestDetails;
  allowCorrectiveRedemption: boolean;
  getFnFEntitlements: () => void;
  getEntitlementShares: () => void;
  setCurrentPage: (page: FNF_PAGES) => void;
  setSelectedEntitlement: (entitlement: Entitlement) => void;
  isLoadingEntitlementShares: boolean;
  entitlementSharesError: boolean;
  entitlementShares: EntitlementShare[];
}

/**
 * A dynamic table for displaying a guests passes and selecting an entitlement to redeem
 * @param props
 * @constructor
 */
export const FnFEntitlementsTable = (props: Props) => {
  const [passEntitlements, setPassEntitlements] = useState<Entitlement[]>([]);
  
  // getGuestHotlists returns a new map each call; compare by value to avoid a render loop
  const hotlists = useSelector<IAppState, ImmutableMap<EntitlementHotlist[]>>(getGuestHotlists, ImmutableMap.equals);
  const isLoadingEntitlements = useSelector((state: IAppState) =>
    !!getLoading(state, props.guest.masterGuestId, ACTION_TYPE.GET_FNF_ENTITLEMENTS_REQUEST)
  );
  const resources = useSelector((state: IAppState) => state.locale);

  const entitlements = useMemo(() =>
    sortByExpirationDateAsc(
      (props.guest.friendsAndFamily || [])
        .filter(ent => isActive(ent) && !isDeferred(ent) && !fnfIsHotlisted(ent, props.guest, hotlists))
    ),
    [props.guest, hotlists]
  );
  
  const noEntitlements = passEntitlements.length === 0 && !isLoadingEntitlements;

  useEffect(() => {
    if (entitlements.length > 0) {
      setPassEntitlements(entitlements);
    }
  }, [isLoadingEntitlements, entitlements])


  const renderRow = (entitlement: Entitlement) => (
    <FnFEntitlementsRow
      key={entitlement.entitlementId}
      guest={props.guest}
      entitlement={entitlement}
      allowCorrectiveRedemption={props.allowCorrectiveRedemption}
      entitlementShares={props.entitlementShares}
      isLoadingEntitlementShares={props.isLoadingEntitlementShares}
      entitlementSharesError={props.entitlementSharesError}
      setCurrentPage={props.setCurrentPage}
      setSelectedEntitlement={props.setSelectedEntitlement}
      resources={resources}
    />
  );

  return (
    <div css={tableStyle}>
      {
        passEntitlements.length > 0  &&
        <table css={css`width: 100%`}>
          <thead>
          <tr>
            <th>{getResource(resources, 'FNF_TABLE_ENTITLEMENT_NAME')}</th>
            <th>{getResource(resources, 'FNF_TABLE_DAYS_AVAILABLE')}</th>
            <th>{getResource(resources, 'FNF_TABLE_DAYS_USED')}</th>
            <th>{getResource(resources, 'FNF_TABLE_ACTION')}</th>
          </tr>
          </thead>
          <tbody>
            {passEntitlements.map(renderRow)}
          </tbody>
        </table>
      }
      {
        noEntitlements
        && <div css={noEntitlements}>{getResource(resources, 'FNF_TABLE_NO_ENTITLEMENTS')}</div>
      }
    </div>
  );
};
